import React, { useEffect, useRef, useState } from 'react'

import { useSnackbar } from 'contexts'
import { useUserProfileQuery, useUserProfileMutations } from 'hooks'
import { useUserPreferencesQuery, useUserPreferencesMutation } from 'hooks'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { PRODUCT_RULES } from 'validation/product'

import { Typography, Stack, Divider, Box } from '@mui/material'
import { LogOutIcon, TrashIcon } from 'lucide-react'

import { Input, Button, Switch } from '@repo/ui'
import { AvatarField, DeleteAccountModal, Loader, SettingsSection } from 'components'
import { useAuth, useCanAccess } from 'hooks'
import type { Profile } from 'types'

type PersonalGeneralFormData = {
  first_name: string
  last_name: string
  phone: string
  title: string
  department: string
  avatar: {
    file: File | null
    preview_url: string
    isUploaded: boolean
  }
}

const DEFAULT_AVATAR_VALUE = {
  file: null,
  preview_url: '',
  isUploaded: false,
}

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message
  }

  return fallbackMessage
}

// Phone is optional. When provided, accept international formats — an optional
// leading "+" plus 7–15 digits, allowing spaces, dashes, parens and dots as
// separators (e.g. "+380501234567", "+1 (555) 123-4567"). Invalid input blocks
// the profile Save via react-hook-form (handleSubmit won't fire onSubmit).
const validatePhone = (value?: string) => {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return true
  const digits = trimmed.replace(/\D/g, '')
  const onlyAllowedChars = /^\+?[\d\s\-().]+$/.test(trimmed)
  if (!onlyAllowedChars || digits.length < 7 || digits.length > 15) {
    return 'Enter a valid phone number (e.g. +380501234567)'
  }
  return true
}

const toFormDefaultValues = (profile: Profile): PersonalGeneralFormData => ({
  first_name: profile.first_name || '',
  last_name: profile.last_name || '',
  phone: profile.phone || '',
  title: profile.title || '',
  department: profile.department || '',
  avatar: {
    file: null,
    preview_url: profile.avatar || '',
    isUploaded: Boolean(profile.avatar),
  },
})

export const ProfilePage = () => {
  // Members who can't manage other members' roles get guidance instead of an
  // editable-looking field (proxy: can invite/manage admins → can manage roles).
  const { isHasAccess: canManageRoles } = useCanAccess({ action: 'users_invite' })
  const { open: notification } = useSnackbar()
  const { logout } = useAuth()
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = React.useState(false)
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false)

  // Personal preferences backed by the company_user `preferences` field.
  const { preferences } = useUserPreferencesQuery()
  const { update: updatePreferences, isSaving: isSavingPreferences } = useUserPreferencesMutation()
  const saveStateOnLogin = Boolean(preferences.saveStateOnLogin)

  // Fetch user profile
  const { data: profile, isLoading: isLoadingProfile } = useUserProfileQuery()

  // Track the last profile ID we've initialized the form with
  const initializedProfileIdRef = useRef<string | null>(null)

  const methods = useForm<PersonalGeneralFormData>({
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      title: '',
      department: '',
      avatar: DEFAULT_AVATAR_VALUE,
    },
  })
  const {
    handleSubmit: handleProfileSubmit,
    formState: { isDirty: isProfileDirty, errors: profileErrors },
    reset,
    control: profileControl,
    setValue,
  } = methods

  // Profile mutation hook for updates
  const { updateAsync: updateProfileAsync, removeAvatarAsync, isUpdating, isRemovingAvatar } = useUserProfileMutations()

  // Reset form with profile data when it loads (only when profile ID changes)
  useEffect(() => {
    if (profile && profile.id && initializedProfileIdRef.current !== profile.id) {
      const formDefaultValues = toFormDefaultValues(profile)

      reset(formDefaultValues)
      initializedProfileIdRef.current = profile.id
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id])

  // Handle form submission
  const onSubmit = async (formData: PersonalGeneralFormData) => {
    if (!profile) {
      return
    }

    const updatePayload = {
      first_name: formData.first_name || profile.first_name,
      last_name: formData.last_name || profile.last_name,
      phone: formData.phone?.trim() ? formData.phone.trim() : null,
      title: formData.title?.trim() ? formData.title.trim() : null,
      department: formData.department?.trim() ? formData.department.trim() : null,
      avatar: formData.avatar,
    }

    setIsSubmittingProfile(true)
    try {
      const updatedProfile = await updateProfileAsync(updatePayload)
      reset(toFormDefaultValues(updatedProfile))
      notification({
        type: 'success',
        title: 'Profile Updated',
        text: 'Your profile has been successfully updated',
      })
    } catch (error) {
      notification({
        type: 'error',
        title: 'Update Failed',
        text: getErrorMessage(error, 'Failed to update profile'),
      })
    } finally {
      setIsSubmittingProfile(false)
    }
  }

  const handleDeleteAccount = () => {
    setOpenDeleteAccountModal(true)
  }

  const handleToggleSaveState = async (next: boolean) => {
    try {
      // The toggle moving IS the confirmation (visible state change) — no success
      // toast, per the toast-usage policy (success of a visible action → no toast).
      await updatePreferences({ saveStateOnLogin: next })
    } catch (error) {
      notification({
        type: 'error',
        title: 'Could not save preference',
        text: getErrorMessage(error, 'Please try again.'),
      })
    }
  }


  // Show loading state
  if (isLoadingProfile) {
    return (
      <Loader/>
    )
  }

  // Show message if no profile is found
  if (!profile) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="400px">
        <Typography variant="body1" color="text.secondary">
          Unable to load profile. Please try again.
        </Typography>
      </Stack>
    )
  }

  // Initials placeholder for the avatar — parity with the sidebar user avatar.
  const avatarInitials =
    [profile.first_name, profile.last_name]
      .filter(Boolean)
      .map(part => part?.[0]?.toUpperCase() ?? '')
      .join('') || (profile.email?.[0]?.toUpperCase() ?? '')

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleProfileSubmit(onSubmit)}>
        {/* spacing 8 (32px), not 4. Every section is a panel again, but a BORDERLESS
            one, so the 1px outline that used to mark where one ended is gone and the
            gap is what separates them. 16px was enough when a border did that job. */}
        <Stack spacing={8}>
          <SettingsSection title="Personal">
                <Controller
                  name="avatar"
                  control={profileControl}
                  defaultValue={{ file: null, preview_url: profile.avatar || '', isUploaded: Boolean(profile.avatar) }}
                  render={({ field, fieldState }) => (
                    <AvatarField
                      label="Profile Photo"
                      name="avatar"
                      initials={avatarInitials}
                      value={field.value}
                      onChange={async (value) => {
                        const previousAvatarValue = field.value || DEFAULT_AVATAR_VALUE
                        const isRemovingPersistedAvatar = Boolean(
                          previousAvatarValue?.isUploaded &&
                          previousAvatarValue?.preview_url &&
                          !value.preview_url &&
                          !value.file
                        )

                        field.onChange(value)

                        // New photo selected → save it immediately (don't wait
                        // for the Save button). On success the profile query is
                        // invalidated, so the sidebar avatar updates too.
                        if (value.file) {
                          try {
                            await updateProfileAsync({ avatar: value })
                            setValue(
                              'avatar',
                              { file: null, preview_url: value.preview_url, isUploaded: true },
                              { shouldDirty: false },
                            )
                            // No success toast: the new photo is already on screen, so a
                            // toast would only restate what the user can see (toast policy —
                            // success-of-a-visible-action gets no toast). The failure branch
                            // below keeps its toast, since a revert is NOT self-explanatory.
                          } catch (error) {
                            setValue('avatar', previousAvatarValue, { shouldDirty: false })
                            notification({
                              type: 'error',
                              title: 'Upload failed',
                              text: getErrorMessage(error, 'Failed to save profile photo'),
                            })
                          }
                          return
                        }

                        if (!isRemovingPersistedAvatar) {
                          return
                        }

                        try {
                          await removeAvatarAsync()
                          // Avatar visibly disappears + sidebar updates optimistically
                          // — no success toast, per the toast-usage policy.
                          setValue('avatar', DEFAULT_AVATAR_VALUE, { shouldDirty: false })
                        } catch (error) {
                          setValue('avatar', previousAvatarValue, { shouldDirty: false })
                          notification({
                            type: 'error',
                            title: 'Avatar Removal Failed',
                            text: getErrorMessage(error, 'Failed to remove profile photo'),
                          })
                        }
                      }}
                      error={fieldState.error?.message}
                      disabled={isUpdating || isRemovingAvatar}
                      {...methods}
                    />
                  )}
                />
                <Divider />
                <Stack pt={2} pb={2} direction="row" spacing={6}>
                  <Stack sx={{ flex: 1 }}>
                    <Controller
                      name="first_name"
                      control={profileControl}
                      defaultValue={profile.first_name || ''}
                      rules={PRODUCT_RULES.title}
                      render={({ field, fieldState }) => (
                        <Stack spacing={2}>
                          <Typography variant="body3">First Name</Typography>
                          <Input
                            {...field}
                            value={field.value}
                            onValueChange={(value) => field.onChange(value)}
                            error={!!fieldState.error}
                          />
                          {fieldState.error && (
                            <Typography variant="body4" color="error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    />
                  </Stack>
                  <Stack sx={{ flex: 1 }}>
                    <Controller
                      name="last_name"
                      control={profileControl}
                      defaultValue={profile.last_name || ''}
                      rules={PRODUCT_RULES.title}
                      render={({ field, fieldState }) => (
                        <Stack spacing={2}>
                          <Typography variant="body3">Last Name</Typography>
                          <Input
                            {...field}
                            value={field.value}
                            onValueChange={(value) => field.onChange(value)}
                            error={!!fieldState.error}
                          />
                          {fieldState.error && (
                            <Typography variant="body4" color="error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    />
                  </Stack>
                </Stack>
                <Stack pb={2} direction="row" spacing={6}>
                  <Stack sx={{ flex: 1 }}>
                    <Controller
                      name="title"
                      control={profileControl}
                      defaultValue={profile.title || ''}
                      render={({ field, fieldState }) => (
                        <Stack spacing={2}>
                          <Typography variant="body3">Title</Typography>
                          <Input
                            {...field}
                            value={field.value}
                            onValueChange={(value) => field.onChange(value)}
                            error={!!fieldState.error}
                          />
                          {fieldState.error && (
                            <Typography variant="body4" color="error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    />
                  </Stack>
                  <Stack sx={{ flex: 1 }}>
                    {/* TODO(VERA-248 / depends on VERA-816): replace this free-text
                        Department Input with the creatable <Combobox> shipped on the
                        UserPage in agent/VERA-816 (options derived from existing
                        company members' departments). Blocked until VERA-816 merges
                        to develop. See context/tech-debt.md (TD-050). */}
                    <Controller
                      name="department"
                      control={profileControl}
                      defaultValue={profile.department || ''}
                      render={({ field, fieldState }) => (
                        <Stack spacing={2}>
                          <Typography variant="body3">Department</Typography>
                          <Input
                            {...field}
                            value={field.value}
                            onValueChange={(value) => field.onChange(value)}
                            error={!!fieldState.error}
                          />
                          {fieldState.error && (
                            <Typography variant="body4" color="error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    />
                  </Stack>
                </Stack>
                <Stack pb={2} direction="row" spacing={6}>
                  <Stack sx={{ flex: 1 }}>
                    <Controller
                      name="phone"
                      control={profileControl}
                      defaultValue={profile.phone || ''}
                      rules={{ validate: validatePhone }}
                      render={({ field, fieldState }) => (
                        <Stack spacing={2}>
                          <Typography variant="body3">Phone</Typography>
                          <Input
                            {...field}
                            value={field.value}
                            onValueChange={(value) => field.onChange(value)}
                            error={!!fieldState.error}
                            type="tel"
                          />
                          {fieldState.error && (
                            <Typography variant="body4" color="error">
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    />
                  </Stack>
                  <Stack sx={{ flex: 1 }}>
                    {/* Role is read-only on the personal page — it's the user's own
                        role, changed elsewhere (Settings → Users / Roles & permissions).
                        Rendered as plain text (not an input) so it doesn't imply it's
                        editable; members without role-management permission get guidance.
                        Label sits at the top (inline with the Phone label); the value is
                        vertically centered in the field area to line up with the input. */}
                    <Stack spacing={2} sx={{ flex: 1 }}>
                      <Typography variant="body3">Role</Typography>
                      <Stack spacing={1} sx={{ flex: 1, justifyContent: 'center' }}>
                        <Typography variant="body2">
                          {profile.role?.name
                            ? profile.role.name
                                .split('_')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                            : '—'}
                        </Typography>
                        {!canManageRoles && (
                          <Typography variant="body4" color="text.secondary">
                            Contact your company admin to change your role.
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack pb={2} direction="row" spacing={3}>
                  {/* NOT destructive. Logging out destroys nothing — it ends a session, and
                      logging back in undoes it; §38's test ("does a row disappear?") fails.
                      This carried `destructive="hover"` while the sidebar's Log out menu row
                      deliberately did not, so one action had two severities depending on where you
                      found it — and the sidebar's own comment asserted this button was already
                      plain, which was not true. Now it is. */}
                  <Button
                    onClick={logout}
                    variant='secondary'
                    disableElevation
                    startIcon={<LogOutIcon strokeWidth={1}/>}
                  >Log out</Button>
                </Stack>
                <Divider />
                {/* Save/Cancel buttons */}
                <Stack direction="row" justifyContent="flex-end" spacing={2} pt={2}>
                  {/* Not gated on `!isProfileDirty` (working-conventions §52). Save
                      reflects whether work exists; Cancel must not go dead just because
                      none does — a page where BOTH buttons are inert on arrival reads as
                      broken, which is exactly how this one read.

                      Still disabled while a submit or avatar removal is in flight: that
                      is transient state, not record state, and cancelling mid-request
                      would race the mutation (§46). */}
                  <Button
                    variant="secondary"
                    onClick={() => reset()}
                    disabled={isSubmittingProfile || isRemovingAvatar}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!isProfileDirty || isSubmittingProfile || isRemovingAvatar || Object.keys(profileErrors).length > 0}
                  >
                    {isSubmittingProfile ? 'Saving...' : 'Save'}
                  </Button>
                </Stack>
          </SettingsSection>
          {/* "Login" section (email + password) MOVED to Settings → Security
              in full (VRG-490, D7 extended to email) — Profile is who you
              are, Security is how you prove it's you: the login email
              receives sign-in codes and password resets, same class as
              password and 2FA. See SecurityPage.tsx for both moved cards.
              Nothing is duplicated here; this section is gone, not emptied. */}
          {/* HIDDEN(TD-053): "Connected accounts" was a non-functional mock —
              WorkOS has no per-provider connect/disconnect API and the profile
              exposes no linked-identity data, so the toggles couldn't be made real.
              Social login now lives on the sign-in page: Google live, Apple behind
              VITE_ENABLE_APPLE_LOGIN (off until Vera owns an Apple Services ID), and
              Microsoft REMOVED outright — WorkOS's Microsoft OAuth is personal-accounts
              -only by design, so it rejected every work account. Re-add a real
              "Sign-in methods" view
              once the backend surfaces WorkOS identities, or build true
              connect/disconnect. See context/tech-debt.md (TD-053). */}
          {/* One toggle today. It gets the same panel as every other section: the panel
              is a surface, not a claim that its contents form a set, so a section of one
              is no longer an exception to explain. When a second preference lands, these become
              sibling rows separated by a divider. */}
          <SettingsSection title="Preferences">
                {/* Stored on the company_user record. On/off state is conveyed by the
                    Switch alone, so the row is not brand-highlighted (would be redundant).

                    The section keeps the name "Preferences" rather than being renamed
                    after its one row, because it is the extension point. When a second
                    preference lands it becomes a sibling row here, separated by a
                    divider — not by a border around each. */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack spacing={1} sx={{ pr: 4 }}>
                    <Typography variant="body2">
                      Save state on next login
                    </Typography>
                    <Typography variant="body3" color="text.secondary">
                      Remember your current filters, sort and table view, and restore them automatically the next time you log in.
                    </Typography>
                  </Stack>
                  <Switch
                    checked={saveStateOnLogin}
                    disabled={isSavingPreferences}
                    onChange={(_, checked) => handleToggleSaveState(checked)}
                    inputProps={{ 'aria-label': 'Save state on next login' }}
                  />
                </Stack>
          </SettingsSection>
          {/* Theme MOVED to Settings → Personal → Appearance (VRG-248), not copied.
              This page is who you are — photo, name, contact, login. How the app
              looks on this screen was never part of that, and it now has a sibling
              (Text size) that would have had nowhere sensible to sit here. */}
          {/* Deliberately last. It now shares the panel treatment with every other
              section — what keeps it quiet is its position and the hover-only destructive
              button (§32), not a different container. */}
          <SettingsSection
            title="Delete account"
            description="Deleted accounts cannot be restored."
          >
                <Button
                  variant='secondary'
                  destructive="hover"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={handleDeleteAccount}
                  startIcon={<TrashIcon strokeWidth={1}/>}>
                  Delete
                </Button>
          </SettingsSection>
        </Stack>
      </form>
      <DeleteAccountModal
        user={profile}
        open={openDeleteAccountModal}
        onClose={() => setOpenDeleteAccountModal(false)}
      />
    </FormProvider>
  )
}
